import {
  isCarePlusPlanCart,
  resolveCarePlusDiscount,
  resolveCarePlusPriceDisplay,
  resolveCarePlusShippingSchedule,
} from "./carePlusCartTotals";
import { DisplayedPriceComponent } from "../../utils/helpers";

export type CarePlusCart = {
  care_plus_plan_fee?: number | string | null;
  care_plus_plan_tier?: string | null;
  care_plus_plan_period_months?: number | string | null;
  care_plus_plan_uses_product_fees?: boolean | null;
  care_plus_plan_image?: { base_path: string; name: string } | null;
  care_plus_plan_fee_breakdown?: { treatment_fee?: number | string | null; service_fee?: number | string | null; shipping_fee?: number | string | null } | null;
  care_plus_discount_type?: "first_month" | "recurring" | string | null;
  applied_subscription_discount?: number | string | null;
  bonus_value?: number | string | null;
  treatment_fee?: number | string | null;
  service_fee?: number | string | null;
  regular_shipping_fee?: number | string | null;
  custom_shipping_fee?: number | string | null;
  care_plus_waived_fees?: string[] | null;
  selected_service_option?: string | null;
  displayed_price_components?: DisplayedPriceComponent[] | null;
  only_prescription?: boolean;
  products?: Array<{
    official_price?: number | string | null;
    service_fee?: number | string | null;
    shipping_fee?: number | string | null;
    quantity?: number | string | null;
    is_addon?: boolean;
  }>;
};

/**
 * HL-11359: mid-term renewals on prepaid multi-month plans charge med + ship only.
 * Zero next-month treatment/service so the card matches SubscriptionsManager.
 */
export const resolveCarePlusNextMonthPlanFees = (cart: {
  care_plus_plan_period_months?: number | string | null;
  care_plus_plan_fee_breakdown?: {
    treatment_fee?: number | string | null;
    service_fee?: number | string | null;
  } | null;
}): {
  periodMonths: number;
  monthlyTreatmentFee: number;
  monthlyServiceFee: number;
} => {
  const periodMonths = Math.max(Number(cart.care_plus_plan_period_months ?? 1) || 1, 1);
  if (periodMonths > 1) {
    return { periodMonths, monthlyTreatmentFee: 0, monthlyServiceFee: 0 };
  }
  return {
    periodMonths,
    monthlyTreatmentFee: Number(cart.care_plus_plan_fee_breakdown?.treatment_fee ?? 0),
    monthlyServiceFee: Number(cart.care_plus_plan_fee_breakdown?.service_fee ?? 0),
  };
};

/**
 * Single source for the Care+ cart card numbers on Shipping, Register and Payment. Everything
 * except the translator, the medication line and the currency comes from here, so the three pages
 * cannot drift. `isCarePlusOrder` is the page's own signal (shipping API flag, care_plus marker,
 * plan fee on the payment cart); a cart with a plan fee is always a Care+ order.
 *
 * Money rules (same as OrderManagerV2):
 * - Only a PLAN order (care_plus_plan_fee > 0) bundles treatment + service into the plan fee. A
 *   one-time purchase keeps them as their own rows.
 * - Today's shipping is the schedule fee: 0 on self-pickup, the express quote on express, else the
 *   cart's regular DHL fee. It stays RAW even when the plan bonus waives it, because the waiver money
 *   already arrives inside bonus_value, which the discount row subtracts in full.
 * - Prescription-only: no medication and no plan, the customer still pays treatment + shipping.
 */
export const getCarePlusShippingCartProps = (cart: CarePlusCart, isCarePlusOrderSignal: boolean) => {
  const isCarePlusPlanOrder = isCarePlusPlanCart(cart);
  const isCarePlusOrder = isCarePlusOrderSignal || isCarePlusPlanOrder;
  const onlyPrescription = Boolean(cart.only_prescription);
  const hasMedication = isCarePlusOrder && !onlyPrescription;
  const products = cart.products ?? [];

  const planFee = Number(cart.care_plus_plan_fee ?? 0);
  // HL-12639: a plan priced from the product's own fees (UK Monthly) shows them inside the medication
  // price, like a One-time order; its plan row is delivery only. Amounts charged are unchanged.
  const isPlanFeeInMedication = isCarePlusPlanOrder && Boolean(cart.care_plus_plan_uses_product_fees);
  const planRowFee = isPlanFeeInMedication ? 0 : planFee;
  const discount = resolveCarePlusDiscount(cart);
  const officialPriceTotal = hasMedication
    ? products.reduce((sum, p) => sum + Number(p.official_price ?? 0) * Number(p.quantity ?? 1), 0)
    : 0;
  const treatmentFee = hasMedication && !isCarePlusPlanOrder ? Number(cart.treatment_fee ?? 0) : 0;
  const serviceFee = hasMedication && !isCarePlusPlanOrder ? Number(cart.service_fee ?? 0) : 0;
  const productShippingTotal = products.reduce((sum, p) => sum + Number(p.shipping_fee ?? 0), 0);
  const shippingSchedule = resolveCarePlusShippingSchedule({
    selectedServiceOption: cart.selected_service_option,
    productShippingFee: hasMedication ? productShippingTotal : 0,
    regularShippingFee: cart.regular_shipping_fee,
    expressShippingFee: cart.custom_shipping_fee,
    breakdownShippingFee: cart.care_plus_plan_fee_breakdown?.shipping_fee != null
      ? Number(cart.care_plus_plan_fee_breakdown.shipping_fee)
      : null,
  });
  const shippingFee = hasMedication ? shippingSchedule.todayFee : 0;
  const prescriptionOnlyTotal = isCarePlusOrder && onlyPrescription
    ? Number(cart.treatment_fee ?? 0) + productShippingTotal
    : 0;

  const { periodMonths, monthlyTreatmentFee, monthlyServiceFee } = resolveCarePlusNextMonthPlanFees(cart);
  const oneTimePriceDisplay = resolveCarePlusPriceDisplay(
    { official_price: officialPriceTotal, treatment_fee: treatmentFee, service_fee: serviceFee, shipping_fee: shippingFee },
    cart.displayed_price_components,
  );
  const monthlyPriceDisplay = resolveCarePlusPriceDisplay(
    { official_price: officialPriceTotal, treatment_fee: monthlyTreatmentFee, service_fee: monthlyServiceFee, shipping_fee: shippingSchedule.recurringFee },
    cart.displayed_price_components,
  );
  // Prepaid multi-month: next charge is med + ship only. Hide the zeroed plan fee rows.
  const showNextMonthPlanFees = periodMonths <= 1;

  const finalPrice = officialPriceTotal + treatmentFee + serviceFee + shippingFee + prescriptionOnlyTotal + planFee - discount;

  const cardProps = {
    planFee,
    planTier: cart.care_plus_plan_tier ?? "",
    periodMonths,
    planImage: cart.care_plus_plan_image ?? null,
    planRowFee,
    // Plan row = plan fee + today's shipping; a plan order shows no standalone delivery row.
    planDisplayPrice: planRowFee + shippingFee,
    total: finalPrice + discount,
    discount,
    finalPrice,
    // A plan order's Today medication line is medication only; displayed_price_components only
    // shapes its recurring breakdown. A one-time order applies the setting to Today as well.
    medicationTotal: isPlanFeeInMedication
      ? officialPriceTotal + planFee
      : isCarePlusPlanOrder ? officialPriceTotal : oneTimePriceDisplay.medicationTotal,
    monthlyMedicationTotal: isPlanFeeInMedication
      ? officialPriceTotal + monthlyTreatmentFee + monthlyServiceFee
      : monthlyPriceDisplay.medicationTotal,
    treatmentFee,
    serviceFee,
    shippingFee,
    monthlyTreatmentFee: isPlanFeeInMedication ? 0 : monthlyTreatmentFee,
    monthlyServiceFee: isPlanFeeInMedication ? 0 : monthlyServiceFee,
    monthlyShippingFee: shippingSchedule.recurringFee,
    showTreatmentFee: oneTimePriceDisplay.showTreatmentFee,
    showServiceFee: oneTimePriceDisplay.showServiceFee,
    showShippingFee: oneTimePriceDisplay.showShippingFee,
    showMonthlyTreatmentFee: showNextMonthPlanFees && monthlyPriceDisplay.showTreatmentFee,
    showMonthlyServiceFee: showNextMonthPlanFees && monthlyPriceDisplay.showServiceFee,
    showMonthlyShippingFee: monthlyPriceDisplay.showShippingFee,
    isPrescriptionOnly: onlyPrescription,
    discountType: (cart.care_plus_discount_type ?? null) as "first_month" | "recurring" | null,
  };

  return { isCarePlusOrder, cardProps };
};
